import { useTranslation } from 'react-i18next'
import { ArrowRight, Lightbulb } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'

interface ProductImprovementsProps {
  improvements: AuditReport['product_improvements']
}

export function ProductImprovements({ improvements }: ProductImprovementsProps) {
  const { t } = useTranslation()
  if (!improvements?.length) return null

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-neutral-900 dark:text-white">{t('amazonListing.report.productImprovements')}</h3>
      {improvements.map((item, i) => (
        <div key={i} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h4 className="font-medium text-sm text-neutral-900 dark:text-white">{item.title}</h4>
                <ImpactBadge impact={item.impact} t={t} />
                <DifficultyBadge difficulty={item.difficulty} t={t} />
              </div>
              {item.current_state && item.target_state && (
                <div className="flex items-center gap-2 text-xs mb-2">
                  <span className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-2 py-1 rounded">{item.current_state}</span>
                  <ArrowRight className="w-3 h-3 text-neutral-400" />
                  <span className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-1 rounded">{item.target_state}</span>
                </div>
              )}
              {item.steps?.length > 0 && (
                <ol className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1 list-decimal list-inside">
                  {item.steps.map((step, j) => <li key={j}>{step}</li>)}
                </ol>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function normalizeLevel(val: string): string {
  const num = Number(val)
  if (!isNaN(num)) {
    if (num >= 4) return 'high'
    if (num >= 2) return 'medium'
    return 'low'
  }
  return val
}

function ImpactBadge({ impact, t }: { impact: string; t: ReturnType<typeof useTranslation>['t'] }) {
  const level = normalizeLevel(impact)
  const colors: Record<string, string> = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400',
  }
  return <span className={`px-2 py-0.5 rounded-full text-xs ${colors[level] || colors.medium}`}>{t('amazonListing.report.impact')}: {t(`amazonListing.report.level.${level}`)}</span>
}

function DifficultyBadge({ difficulty, t }: { difficulty: string; t: ReturnType<typeof useTranslation>['t'] }) {
  const level = normalizeLevel(difficulty)
  const colors: Record<string, string> = {
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }
  return <span className={`px-2 py-0.5 rounded-full text-xs ${colors[level] || colors.medium}`}>{t('amazonListing.report.difficulty')}: {t(`amazonListing.report.level.${level}`)}</span>
}
